import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Box,
} from '@mui/material';
import TextFieldRenderer from './field-renderers/TextFieldRenderer';
import ButtonFieldRenderer from './field-renderers/ButtonFieldRenderer';
import DropdownFieldRenderer from './field-renderers/DropdownFieldRenderer';
import { FIELD_TYPES } from './fieldHelpers';
import RadioFieldRenderer from './field-renderers/RadioFieldRenderer';
import FileUploadFieldRenderer from "./field-renderers/FileUploadFieldRenderer";

// Stable default values to prevent infinite re-renders
const DEFAULT_VALUES = {};
const DEFAULT_FIELDS = [];

/**
 * Enhanced confirmation dialog with dynamic form fields and validation
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Callback when dialog is closed
 * @param {Function} props.onConfirm - Callback when form is confirmed/submitted
 * @param {string} [props.title="Confirm Action"] - Dialog title
 * @param {string} [props.message="Are you sure you want to proceed?"] - Dialog message
 * @param {string} [props.cancelText="Cancel"] - Cancel button text
 * @param {string} [props.confirmText="Confirm"] - Confirm button text
 * @param {string} [props.confirmColor="primary"] - Confirm button color
 * @param {string} [props.cancelColor="primary"] - Cancel button color
 * @param {string} [props.confirmVariant="contained"] - Confirm button variant
 * @param {string} [props.cancelVariant="text"] - Cancel button variant
 * @param {Array} [props.fields=[]] - Array of field configuration objects
 * @param {Object} [props.defaultValues={}] - Default form values
 * @param {boolean} [props.showMessage=true] - Whether to show the message text
 * @param {boolean} [props.returnChangedFieldsOnly=false] - If true, onConfirm receives only changed fields
 *
 * @example
 * <EnhancedConfirmationDialog
 *   open={true}
 *   onClose={() => {}}
 *   onConfirm={(data) => console.log(data)}
 *   title="Add User"
 *   fields={[
 *     { name: 'firstName', label: 'First Name', type: 'text', required: true },
 *     { name: 'role', label: 'Role', type: 'dropdown', options: [...], multiple: true }
 *   ]}
 *   defaultValues={{ firstName: '', role: [] }}
 * />
 */
const EnhancedConfirmationDialog = ({
    open,
    onClose,
    onConfirm,
    title = "Confirm Action",
    message = "Are you sure you want to proceed?",
    cancelText = "Cancel",
    confirmText = "Confirm",
    confirmColor = "primary",
    cancelColor = "primary",
    confirmVariant = "contained",
    cancelVariant = "text",
    fields = DEFAULT_FIELDS,
    defaultValues = DEFAULT_VALUES,
    showMessage = true,
    returnChangedFieldsOnly = false,
}) => {
    const {
        control,
        handleSubmit,
        reset,
        formState: { dirtyFields },
    } = useForm({
        defaultValues,
        mode: 'onSubmit',
    });

    // Reset form when dialog opens with new default values
    useEffect(() => {
        if (open) {
            reset(defaultValues);
        }
    }, [open, defaultValues, reset]);

    /**
     * Handles form submission
     * @param {Object} data - Form data from React Hook Form
     */
    const onSubmit = (data) => {
        if (returnChangedFieldsOnly) {
            // Return only the fields that have changed
            const changedData = Object.keys(dirtyFields).reduce((acc, key) => {
                acc[key] = data[key];
                return acc;
            }, {});
            onConfirm(changedData, data); // Pass both changed and full data
        } else {
            onConfirm(data);
        }
    };

    /**
     * Handles confirm button click for dialogs without fields
     */
    const handleConfirmClick = () => {
        if (fields.length === 0) {
            onConfirm();
        }
    };

    /**
     * Handles dialog close, resets form
     */
    const handleClose = () => {
        reset();
        onClose();
    };

    /**
     * Renders appropriate field component based on field type
     * @param {Object} field - Field configuration object
     * @returns {JSX.Element} Rendered field component
     */
    const renderField = (field) => {
        const { type = 'text' } = field;

        switch (type) {
            case FIELD_TYPES.BUTTON:
                return <ButtonFieldRenderer key={field.name} field={field} />;

            case FIELD_TYPES.SELECT:
            case FIELD_TYPES.DROPDOWN:
                return <DropdownFieldRenderer key={field.name} field={field} control={control} />;

            case FIELD_TYPES.RADIO:
                return <RadioFieldRenderer key={field.name} field={field} control={control} />;

            case FIELD_TYPES.FILE:
                return <FileUploadFieldRenderer key={field.name} field={field} control={control} />;

            case FIELD_TYPES.TEXT:
            case FIELD_TYPES.NUMBER:
            case FIELD_TYPES.EMAIL:
            default:
                return <TextFieldRenderer key={field.name} field={field} control={control} />;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            disableRestoreFocus
            disableEnforceFocus
            aria-labelledby="confirmation-dialog-title"
            aria-describedby="confirmation-dialog-description"
            PaperProps={{
                sx: {
                    position: 'fixed',
                    top: 0,
                    m: 0,
                    minWidth: fields.length > 0 ? '400px' : 'auto',
                }
            }}
        >
            <DialogTitle id="confirmation-dialog-title">
                {title}
            </DialogTitle>
            <DialogContent>
                {showMessage && message && (
                    <DialogContentText
                        id="confirmation-dialog-description"
                        sx={{ mb: fields.length > 0 ? 2 : 0 }}
                    >
                        {message}
                    </DialogContentText>
                )}

                {fields.length > 0 && (
                    <Box
                        component="form"
                        id="confirmation-form"
                        onSubmit={handleSubmit(onSubmit)}
                        noValidate
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            mt: showMessage && message ? 0 : 2,
                        }}
                    >
                        {fields.map((field) => renderField(field))}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleClose}
                    color={cancelColor}
                    variant={cancelVariant}
                >
                    {cancelText}
                </Button>
                <Button
                    type={fields.length > 0 ? "submit" : "button"}
                    form={fields.length > 0 ? "confirmation-form" : undefined}
                    onClick={fields.length === 0 ? handleConfirmClick : undefined}
                    color={confirmColor}
                    variant={confirmVariant}
                    autoFocus
                >
                    {confirmText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EnhancedConfirmationDialog;
